/**
 * Procedural glyphs: one per element (used on HUD tiles, queue slots, mines,
 * status pops), one per status (icons under wizards and on ally cards) and
 * one per spell form (preview cards). Shapes differ, not only hue, so the
 * queue stays readable for colour-blind players (DESIGN 1 "Legible mistakes").
 *
 * All glyphs are drawn centred on (x, y) inside a `size` px box; the caller
 * sets fillStyle / strokeStyle beforehand (so one colour change serves many
 * glyphs).
 */

import type { CastMode, Element, MagickId, SpellKind, StatusId } from '@shared/index.js';
import { WOVEN_INFO, isWoven } from '@shared/index.js';
import { TAU, type Ctx } from './gfx.js';
import { art } from './sprites.js';

/**
 * The art library's pixel icons (games-media: made with Gemini from one style anchor, in the
 * shapes of these glyphs) sit on transparent ground and fill about 80% of their box, so they are
 * drawn this much larger than a glyph to read the same size.
 */
export const ICON_SCALE = 1.25;

export const elementIconName = (el: Element): string => `element-${el}`;
export const magickIconName = (id: MagickId): string => `magick-${id}`;
export const modeIconName = (m: CastMode): string => `mode-${m}`;

/** True once the icons have landed (the HUD then uses darker tiles so full-colour icons read). */
export function iconsReady(): boolean {
  return art.icon(elementIconName('fire')) !== null;
}

/** An element's icon centred on (x, y) for a glyph of `size`; false while the icons load. */
export function drawElementIcon(ctx: Ctx, el: Element, x: number, y: number, size: number, alpha = 1): boolean {
  return art.drawIcon(ctx, elementIconName(el), x, y, size * ICON_SCALE, alpha);
}

/** An element's icon, or until the icons land its vector glyph in the current fill. */
export function drawElementMark(ctx: Ctx, el: Element, x: number, y: number, size: number, alpha = 1): void {
  if (drawElementIcon(ctx, el, x, y, size, alpha)) return;
  const a = ctx.globalAlpha;
  ctx.globalAlpha = a * alpha;
  drawElementGlyph(ctx, el, x, y, size);
  ctx.globalAlpha = a;
}

/** A Magick's or a cast mode's icon for a glyph of `size`; false while the icons load (they have no glyph). */
export function drawMagickIcon(ctx: Ctx, id: MagickId, x: number, y: number, size: number): boolean {
  return art.drawIcon(ctx, magickIconName(id), x, y, size * ICON_SCALE);
}
export function drawModeIcon(ctx: Ctx, mode: CastMode, x: number, y: number, size: number): boolean {
  return art.drawIcon(ctx, modeIconName(mode), x, y, size * ICON_SCALE);
}

function star(ctx: Ctx, x: number, y: number, outer: number, inner: number, points: number, rot: number): void {
  ctx.beginPath();
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = rot + (Math.PI * i) / points;
    const px = x + Math.cos(a) * r;
    const py = y + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
}

/** Fill-only element glyph. */
export function drawElementGlyph(ctx: Ctx, el: Element, x: number, y: number, size: number): void {
  const r = size / 2;
  if (isWoven(el)) {
    // A woven piece has no vector glyph: its two-letter label, until its icon lands.
    ctx.font = `800 ${Math.round(size * 0.62)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(WOVEN_INFO[el].short, x, y + size * 0.04);
    return;
  }
  switch (el) {
    case 'water': {
      // teardrop
      ctx.beginPath();
      ctx.moveTo(x, y - r);
      ctx.bezierCurveTo(x + r * 0.9, y + r * 0.1, x + r * 0.75, y + r, x, y + r);
      ctx.bezierCurveTo(x - r * 0.75, y + r, x - r * 0.9, y + r * 0.1, x, y - r);
      ctx.closePath();
      ctx.fill();
      return;
    }
    case 'life': {
      const t = r * 0.34;
      ctx.beginPath();
      ctx.rect(x - t, y - r, t * 2, r * 2);
      ctx.rect(x - r, y - t, r * 2, t * 2);
      ctx.fill();
      return;
    }
    case 'shield': {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i - Math.PI / 2;
        const px = x + Math.cos(a) * r;
        const py = y + Math.sin(a) * r;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
      return;
    }
    case 'cold': {
      // six-spoke snowflake drawn as thin rects
      ctx.save();
      ctx.translate(x, y);
      const w = Math.max(1, r * 0.22);
      for (let i = 0; i < 3; i++) {
        ctx.rotate(i === 0 ? 0 : Math.PI / 3);
        ctx.fillRect(-w / 2, -r, w, r * 2);
        ctx.fillRect(-r * 0.45, -r * 0.55, r * 0.9, w);
        ctx.fillRect(-r * 0.45, r * 0.55 - w, r * 0.9, w);
      }
      ctx.restore();
      return;
    }
    case 'lightning': {
      ctx.beginPath();
      ctx.moveTo(x + r * 0.25, y - r);
      ctx.lineTo(x - r * 0.45, y + r * 0.1);
      ctx.lineTo(x + r * 0.05, y + r * 0.1);
      ctx.lineTo(x - r * 0.25, y + r);
      ctx.lineTo(x + r * 0.5, y - r * 0.15);
      ctx.lineTo(x + r * 0.02, y - r * 0.15);
      ctx.closePath();
      ctx.fill();
      return;
    }
    case 'arcane': {
      star(ctx, x, y, r, r * 0.4, 4, -Math.PI / 2);
      ctx.fill();
      return;
    }
    case 'earth': {
      // irregular boulder octagon
      ctx.beginPath();
      const pts = [0.95, 0.8, 1, 0.85, 0.9, 0.78, 1, 0.88];
      for (let i = 0; i < 8; i++) {
        const a = (TAU * i) / 8 + 0.3;
        const rr = r * (pts[i] as number);
        const px = x + Math.cos(a) * rr;
        const py = y + Math.sin(a) * rr;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
      return;
    }
    case 'fire': {
      ctx.beginPath();
      ctx.moveTo(x, y - r);
      ctx.bezierCurveTo(x + r * 0.2, y - r * 0.4, x + r, y - r * 0.1, x + r * 0.75, y + r * 0.45);
      ctx.bezierCurveTo(x + r * 0.6, y + r, x - r * 0.6, y + r, x - r * 0.75, y + r * 0.45);
      ctx.bezierCurveTo(x - r, y - r * 0.2, x - r * 0.3, y - r * 0.2, x, y - r);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha *= 0.55;
      ctx.beginPath();
      ctx.ellipse(x, y + r * 0.35, r * 0.3, r * 0.45, 0, 0, TAU);
      ctx.fill();
      ctx.globalAlpha /= 0.55;
      return;
    }
    case 'steam': {
      ctx.beginPath();
      ctx.arc(x - r * 0.45, y + r * 0.2, r * 0.45, 0, TAU);
      ctx.arc(x + r * 0.1, y - r * 0.15, r * 0.6, 0, TAU);
      ctx.arc(x + r * 0.5, y + r * 0.3, r * 0.42, 0, TAU);
      ctx.fill();
      return;
    }
    case 'ice': {
      ctx.beginPath();
      ctx.moveTo(x, y - r);
      ctx.lineTo(x + r * 0.6, y);
      ctx.lineTo(x, y + r);
      ctx.lineTo(x - r * 0.6, y);
      ctx.closePath();
      ctx.fill();
      return;
    }
    case 'mud': {
      // a squat lumpy blob
      ctx.beginPath();
      ctx.ellipse(x, y + r * 0.25, r * 0.95, r * 0.6, 0, 0, TAU);
      ctx.arc(x - r * 0.35, y - r * 0.15, r * 0.38, 0, TAU);
      ctx.arc(x + r * 0.3, y - r * 0.25, r * 0.32, 0, TAU);
      ctx.fill();
      return;
    }
    case 'lava': {
      // a molten drop over a crust line
      ctx.beginPath();
      ctx.moveTo(x, y - r);
      ctx.bezierCurveTo(x + r * 0.95, y, x + r * 0.7, y + r * 0.8, x, y + r * 0.8);
      ctx.bezierCurveTo(x - r * 0.7, y + r * 0.8, x - r * 0.95, y, x, y - r);
      ctx.closePath();
      ctx.fill();
      ctx.fillRect(x - r, y + r * 0.82, r * 2, r * 0.18);
      return;
    }
    case 'spring': {
      // a drop with a leaf-plus: water that heals
      ctx.beginPath();
      ctx.moveTo(x - r * 0.15, y - r);
      ctx.bezierCurveTo(x + r * 0.7, y + r * 0.05, x + r * 0.55, y + r, x - r * 0.15, y + r);
      ctx.bezierCurveTo(x - r * 0.85, y + r, x - r * 1.0, y + r * 0.05, x - r * 0.15, y - r);
      ctx.closePath();
      ctx.fill();
      const t = r * 0.16;
      ctx.fillRect(x + r * 0.55 - t, y - r * 0.9, t * 2, r * 0.8);
      ctx.fillRect(x + r * 0.15, y - r * 0.5 - t, r * 0.8, t * 2);
      return;
    }
  }
}

/** Status icon glyph (fill); the caller draws the duration arc if wanted. */
export function drawStatusGlyph(ctx: Ctx, id: StatusId, x: number, y: number, size: number): void {
  const r = size / 2;
  switch (id) {
    case 'wet': drawElementGlyph(ctx, 'water', x, y, size); return;
    case 'burning': drawElementGlyph(ctx, 'fire', x, y, size); return;
    case 'chilled': drawElementGlyph(ctx, 'cold', x, y, size); return;
    case 'frozen': drawElementGlyph(ctx, 'ice', x, y, size); return;
    case 'shocked': drawElementGlyph(ctx, 'lightning', x, y, size); return;
    case 'knockdown':
      ctx.beginPath();
      ctx.rect(x - r, y - r * 0.25, r * 2, r * 0.5);
      ctx.fill();
      return;
    case 'hasted':
      ctx.beginPath();
      ctx.moveTo(x - r, y - r * 0.6);
      ctx.lineTo(x - r * 0.2, y);
      ctx.lineTo(x - r, y + r * 0.6);
      ctx.lineTo(x - r * 0.6, y + r * 0.6);
      ctx.lineTo(x + r * 0.2, y);
      ctx.lineTo(x - r * 0.6, y - r * 0.6);
      ctx.closePath();
      ctx.moveTo(x + r * 0.05, y - r * 0.6);
      ctx.lineTo(x + r * 0.85, y);
      ctx.lineTo(x + r * 0.05, y + r * 0.6);
      ctx.lineTo(x + r * 0.45, y + r * 0.6);
      ctx.lineTo(x + r * 1.2, y);
      ctx.lineTo(x + r * 0.45, y - r * 0.6);
      ctx.closePath();
      ctx.fill();
      return;
    case 'mired': {
      // boot-deep mud: a low lumpy mound
      ctx.beginPath();
      ctx.ellipse(x, y + r * 0.35, r * 0.95, r * 0.5, 0, 0, TAU);
      ctx.arc(x - r * 0.3, y, r * 0.35, 0, TAU);
      ctx.arc(x + r * 0.35, y - r * 0.05, r * 0.3, 0, TAU);
      ctx.fill();
      return;
    }
    case 'slowed': {
      // clock face: ring + hands
      ctx.save();
      ctx.lineWidth = Math.max(1, r * 0.25);
      ctx.strokeStyle = ctx.fillStyle;
      ctx.beginPath();
      ctx.arc(x, y, r * 0.85, 0, TAU);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, y - r * 0.55);
      ctx.moveTo(x, y);
      ctx.lineTo(x + r * 0.4, y + r * 0.2);
      ctx.stroke();
      ctx.restore();
      return;
    }
  }
}

/**
 * Spell form icon for the preview cards (stroke + fill in the current
 * styles). Beam = line, spray = cone, arc = zigzag, projectile = dot with
 * trail, nova/quake = rings, barrier/wall = segments, mines = dots, storm =
 * wavy band, self = collapsing ring, bubble = circle, aura = dashed ring,
 * armor = plated hexagon, melee = wedge, wind = chevrons, none = dash.
 */
export function drawFormIcon(ctx: Ctx, kind: SpellKind, x: number, y: number, size: number): void {
  const r = size / 2;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = Math.max(1.5, size * 0.1);
  switch (kind) {
    case 'beam':
      ctx.beginPath(); ctx.moveTo(x - r, y); ctx.lineTo(x + r, y); ctx.stroke();
      ctx.beginPath(); ctx.arc(x + r, y, r * 0.25, 0, TAU); ctx.fill();
      return;
    case 'spray':
      ctx.beginPath(); ctx.moveTo(x - r, y); ctx.lineTo(x + r, y - r * 0.6); ctx.lineTo(x + r, y + r * 0.6); ctx.closePath(); ctx.stroke();
      ctx.beginPath(); ctx.arc(x + r * 0.3, y, r * 0.15, 0, TAU); ctx.arc(x + r * 0.7, y - r * 0.25, r * 0.15, 0, TAU); ctx.fill();
      return;
    case 'arc':
      ctx.beginPath(); ctx.moveTo(x - r, y - r * 0.5); ctx.lineTo(x - r * 0.3, y + r * 0.5); ctx.lineTo(x + r * 0.3, y - r * 0.5); ctx.lineTo(x + r, y + r * 0.5); ctx.stroke();
      return;
    case 'projectile':
      ctx.beginPath(); ctx.arc(x + r * 0.4, y, r * 0.5, 0, TAU); ctx.fill();
      ctx.beginPath(); ctx.moveTo(x - r, y); ctx.lineTo(x - r * 0.3, y); ctx.stroke();
      return;
    case 'nova':
      ctx.beginPath(); ctx.arc(x, y, r * 0.95, 0, TAU); ctx.stroke();
      ctx.beginPath(); ctx.arc(x, y, r * 0.45, 0, TAU); ctx.stroke();
      return;
    case 'quake':
      ctx.beginPath();
      for (let i = 0; i < 12; i++) {
        const a = (TAU * i) / 12;
        const rr = r * (i % 2 === 0 ? 0.95 : 0.7);
        const px = x + Math.cos(a) * rr, py = y + Math.sin(a) * rr;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath(); ctx.stroke();
      return;
    case 'barrier':
    case 'wall':
      ctx.beginPath(); ctx.arc(x, y + r * 0.3, r, -Math.PI * 0.85, -Math.PI * 0.15); ctx.stroke();
      if (kind === 'wall') { ctx.beginPath(); ctx.rect(x - r * 0.3, y - r * 0.1, r * 0.6, r * 0.5); ctx.fill(); }
      return;
    case 'mines':
      ctx.beginPath();
      ctx.arc(x - r * 0.55, y + r * 0.3, r * 0.28, 0, TAU);
      ctx.arc(x, y - r * 0.45, r * 0.28, 0, TAU);
      ctx.arc(x + r * 0.55, y + r * 0.3, r * 0.28, 0, TAU);
      ctx.fill();
      return;
    case 'storm':
      ctx.beginPath();
      for (let i = 0; i <= 8; i++) {
        const px = x - r + (2 * r * i) / 8;
        const py = y + Math.sin(i * 1.3) * r * 0.4;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.stroke();
      return;
    case 'self':
      ctx.beginPath(); ctx.arc(x, y, r * 0.9, 0, TAU); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x, y - r * 0.45); ctx.lineTo(x, y + r * 0.45); ctx.moveTo(x - r * 0.45, y); ctx.lineTo(x + r * 0.45, y); ctx.stroke();
      return;
    case 'bubble':
      ctx.beginPath(); ctx.arc(x, y, r * 0.9, 0, TAU); ctx.stroke();
      ctx.beginPath(); ctx.arc(x, y + r * 0.2, r * 0.3, 0, TAU); ctx.fill();
      return;
    case 'aura':
      ctx.save(); ctx.setLineDash([size * 0.18, size * 0.14]);
      ctx.beginPath(); ctx.arc(x, y, r * 0.9, 0, TAU); ctx.stroke();
      ctx.restore();
      return;
    case 'armor':
      drawElementGlyph(ctx, 'shield', x, y, size * 0.9);
      return;
    case 'melee':
      ctx.beginPath(); ctx.moveTo(x - r * 0.8, y + r * 0.8); ctx.lineTo(x + r * 0.8, y - r * 0.8); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x - r * 0.2, y - r * 0.8); ctx.arc(x, y, r * 0.9, -Math.PI * 0.6, -Math.PI * 0.05); ctx.stroke();
      return;
    case 'wind':
      ctx.beginPath();
      ctx.moveTo(x - r, y - r * 0.4); ctx.lineTo(x - r * 0.4, y - r * 0.4); ctx.lineTo(x, y);
      ctx.moveTo(x - r * 0.4, y + r * 0.5); ctx.lineTo(x + r * 0.4, y + r * 0.5); ctx.lineTo(x + r * 0.8, y + r * 0.1);
      ctx.stroke();
      return;
    case 'none':
      ctx.beginPath(); ctx.moveTo(x - r * 0.5, y); ctx.lineTo(x + r * 0.5, y); ctx.stroke();
      return;
  }
}

/** Draw a remaining-time arc around a status icon (fraction 0..1). */
export function drawTimeArc(ctx: Ctx, x: number, y: number, r: number, frac: number, color: string, width = 2): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.arc(x, y, r, -Math.PI / 2, -Math.PI / 2 + TAU * Math.max(0, Math.min(1, frac)));
  ctx.stroke();
}
